'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Search, MapPin, BookOpen, Star, TrendingUp, Award, Users, ChevronRight, ArrowRight, GraduationCap, Briefcase, Scale, Stethoscope, Cpu, Palette, Sparkles, Globe, Send, CheckCircle, Zap } from 'lucide-react';
import { fetchColleges, College } from '@/lib/services/collegeService';
import { useRouter } from 'next/navigation';

const topCities = [
  { name: 'Delhi', count: 1240, icon: '🏛️', state: 'Delhi' },
  { name: 'Mumbai', count: 980, icon: '🌆', state: 'Maharashtra' },
  { name: 'Bengaluru', count: 1120, icon: '🌿', state: 'Karnataka' },
  { name: 'Chennai', count: 760, icon: '🏖️', state: 'Tamil Nadu' },
  { name: 'Hyderabad', count: 890, icon: '💎', state: 'Telangana' },
  { name: 'Pune', count: 640, icon: '🎓', state: 'Maharashtra' },
  { name: 'Kolkata', count: 720, icon: '🌉', state: 'West Bengal' },
  { name: 'Ahmedabad', count: 480, icon: '🏗️', state: 'Gujarat' },
];

const streamIcons: Record<string, React.ReactNode> = {
  Engineering: <Cpu size={22} />,
  Medical: <Stethoscope size={22} />,
  Management: <Briefcase size={22} />,
  Law: <Scale size={22} />,
  Arts: <Palette size={22} />,
  Commerce: <TrendingUp size={22} />,
};

const topStreams = [
  { name: 'Engineering', colleges: 8200, exam: 'JEE Main / Advanced', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { name: 'Medical', colleges: 3100, exam: 'NEET UG / PG', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  { name: 'Management', colleges: 4500, exam: 'CAT / MAT / XAT', color: 'bg-green-50 text-green-700 border-green-200' },
  { name: 'Law', colleges: 1200, exam: 'CLAT / AILET', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { name: 'Arts', colleges: 5600, exam: 'CUET / State CET', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { name: 'Commerce', colleges: 3800, exam: 'CUET / CA Foundation', color: 'bg-sky-50 text-sky-700 border-sky-200' },
];

const teRankings = [
  { rank: 1, name: 'IIT Bombay', score: 98.4, stream: 'Engineering', change: 'up' },
  { rank: 2, name: 'IIT Delhi', score: 97.8, stream: 'Engineering', change: 'same' },
  { rank: 3, name: 'IIM Ahmedabad', score: 97.1, stream: 'Management', change: 'up' },
  { rank: 4, name: 'AIIMS Delhi', score: 96.9, stream: 'Medical', change: 'same' },
  { rank: 5, name: 'NLSIU Bangalore', score: 95.2, stream: 'Law', change: 'up' },
];

const counsellingSteps = [
  { step: '01', title: 'Take Free Assessment', desc: 'Answer 10 questions about your interests, scores & goals' },
  { step: '02', title: 'Get Matched', desc: 'AI matches you with 50+ colleges based on your profile' },
  { step: '03', title: 'Talk to Expert', desc: 'Book a free 30-min session with a certified counsellor' },
  { step: '04', title: 'Apply & Enroll', desc: 'Get guided through application, documents & admission' },
];

const top50Colleges = [
  { rank: 1, name: 'IIT Bombay', location: 'Mumbai', stream: 'Engineering', nirf: 3, fees: '₹2.2L/yr', placement: '₹21.8 LPA', rating: 4.9 },
  { rank: 2, name: 'IIT Delhi', location: 'New Delhi', stream: 'Engineering', nirf: 2, fees: '₹2.1L/yr', placement: '₹20.5 LPA', rating: 4.9 },
  { rank: 3, name: 'IIM Ahmedabad', location: 'Ahmedabad', stream: 'Management', nirf: 1, fees: '₹23L/yr', placement: '₹35.1 LPA', rating: 4.8 },
  { rank: 4, name: 'AIIMS Delhi', location: 'New Delhi', stream: 'Medical', nirf: 1, fees: '₹1.6K/yr', placement: '₹18 LPA', rating: 4.9 },
  { rank: 5, name: 'IIT Madras', location: 'Chennai', stream: 'Engineering', nirf: 1, fees: '₹2.0L/yr', placement: '₹19.2 LPA', rating: 4.8 },
  { rank: 6, name: 'BITS Pilani', location: 'Pilani', stream: 'Engineering', nirf: 26, fees: '₹5.5L/yr', placement: '₹17.5 LPA', rating: 4.7 },
  { rank: 7, name: 'IIM Bangalore', location: 'Bengaluru', stream: 'Management', nirf: 2, fees: '₹24L/yr', placement: '₹33.8 LPA', rating: 4.8 },
  { rank: 8, name: 'NLSIU Bangalore', location: 'Bengaluru', stream: 'Law', nirf: 1, fees: '₹2.8L/yr', placement: '₹14 LPA', rating: 4.7 },
  { rank: 9, name: 'IIT Kharagpur', location: 'Kharagpur', stream: 'Engineering', nirf: 5, fees: '₹1.9L/yr', placement: '₹18.8 LPA', rating: 4.7 },
  { rank: 10, name: 'IISc Bangalore', location: 'Bengaluru', stream: 'Science', nirf: 1, fees: '₹35K/yr', placement: '₹22 LPA', rating: 4.9 },
];

const aiFeatures = [
  { icon: '🎯', title: 'Smart College Matching', desc: 'AI analyses 50+ parameters to find your perfect college fit' },
  { icon: '📊', title: 'Admission Probability', desc: 'Real-time prediction of your admission chances at any college' },
  { icon: '💬', title: '24/7 AI Counsellor', desc: 'Get instant answers to any college or career question' },
  { icon: '🗺️', title: 'Career Path Mapping', desc: 'Visualise your career trajectory from college to senior roles' },
];

// Map top50Colleges names to mockData college IDs
const collegeNameToId: Record<string, string> = {
  'IIT Bombay': 'college-002',
  'IIT Delhi': 'college-001',
  'IIM Ahmedabad': 'college-003',
  'AIIMS Delhi': 'college-004',
  'IIT Madras': 'college-001', // fallback to closest match
  'BITS Pilani': 'college-005',
  'IIM Bangalore': 'college-003', // fallback
  'NLSIU Bangalore': 'college-014',
  'IIT Kharagpur': 'college-001', // fallback
  'IISc Bangalore': 'college-002', // fallback
};

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchStream, setSearchStream] = useState('');
  const [compareList, setCompareList] = useState<number[]>([]);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const [colleges, setColleges] = useState<College[]>([]);
  const router = useRouter();

  useEffect(() => {
    fetchColleges().then(setColleges);
  }, []);

  const topColleges = colleges.slice(0, 6);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (searchCity) params.set('city', searchCity);
    if (searchStream) params.set('stream', searchStream);
    window.location.href = `/?${params.toString()}`;
  };

  const toggleCompare = (rank: number) => {
    setCompareList(prev =>
      prev.includes(rank) ? prev.filter(r => r !== rank) : prev.length < 3 ? [...prev, rank] : prev
    );
  };

  const handleNewsletter = () => {
    if (!newsletterEmail) return;
    setNewsletterSubmitted(true);
  };

  const handleCompareNow = () => {
    const ids = compareList
      .map((rank) => {
        const college = top50Colleges.find((c) => c.rank === rank);
        if (!college) return null;
        // Try to find by name in mockData
        const matched = colleges.find(
          (c) =>
            c.shortName.toLowerCase() === college.name.toLowerCase() ||
            c.name.toLowerCase().includes(college.name.toLowerCase()) ||
            college.name.toLowerCase().includes(c.shortName.toLowerCase())
        );
        if (matched) return matched.id;
        return collegeNameToId[college.name] ?? null;
      })
      .filter(Boolean) as string[];

    if (ids.length >= 2) {
      router.push(`/compare-colleges?ids=${ids.join(',')}`);
    } else {
      router.push('/compare-colleges');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">

        {/* ── HERO BANNER ── */}
        <section className="relative bg-secondary overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-violet-400 blur-3xl translate-y-1/2 -translate-x-1/2" />
          </div>

          <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16 lg:py-24">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm text-slate-200 mb-6 border border-white/20">
                <Sparkles size={14} className="text-primary" />
                <span>AI-Powered · India&apos;s #1 College Discovery Platform · 2M+ Students Guided</span>
              </div>

              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight mb-4">
                Find Your <span className="text-primary">Dream College</span>
                <br />with AI Guidance
              </h1>
              <p className="text-slate-300 text-lg mb-10 max-w-xl mx-auto">
                Explore 25,000+ colleges, compare courses, check cutoffs, and get AI-powered expert counselling — all in one place.
              </p>

              {/* Search Bar */}
              <div className="bg-white rounded-2xl p-2 shadow-2xl max-w-2xl mx-auto">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                    <input
                      type="text"
                      placeholder="Search college, course, exam..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="w-full pl-9 pr-3 py-3 text-sm text-foreground bg-transparent outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                  <div className="relative sm:w-40 border-t sm:border-t-0 sm:border-l border-border">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                    <input
                      type="text"
                      placeholder="City / State"
                      value={searchCity}
                      onChange={(e) => setSearchCity(e.target.value)}
                      className="w-full pl-8 pr-3 py-3 text-sm text-foreground bg-transparent outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                  <div className="relative sm:w-40 border-t sm:border-t-0 sm:border-l border-border">
                    <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                    <select
                      value={searchStream}
                      onChange={(e) => setSearchStream(e.target.value)}
                      className="w-full pl-8 pr-3 py-3 text-sm text-foreground bg-transparent outline-none appearance-none cursor-pointer"
                    >
                      <option value="">All Streams</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Medical">Medical</option>
                      <option value="Management">Management</option>
                      <option value="Law">Law</option>
                      <option value="Arts">Arts</option>
                      <option value="Commerce">Commerce</option>
                    </select>
                  </div>
                  <button onClick={handleSearch} className="btn-primary rounded-xl px-6 py-3 text-sm font-bold whitespace-nowrap">
                    Search
                  </button>
                </div>
              </div>

              {/* Quick stats */}
              <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-slate-300">
                {[['25,000+', 'Colleges'], ['120+', 'Streams'], ['38', 'States & UTs'], ['2M+', 'Students Helped']].map(([num, label], i, arr) => (
                  <React.Fragment key={label}>
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-white">{num}</span> {label}
                    </div>
                    {i < arr.length - 1 && <div className="w-px h-4 bg-white/20" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── AI PLATFORM BANNER ── */}
        <section className="py-10 px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
          <div className="bg-gradient-to-r from-primary via-violet-600 to-purple-700 rounded-3xl p-6 lg:p-8 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white blur-3xl -translate-y-1/2 translate-x-1/4" />
            </div>
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 text-sm text-white mb-4">
                  <Sparkles size={14} />
                  <span>AI-Enabled Future Ready Platform</span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-extrabold text-white mb-3">
                  The Smartest Way to Choose Your College
                </h2>
                <p className="text-white/80 mb-5 text-sm leading-relaxed">
                  Our AI analyses thousands of data points — your scores, interests, budget, and career goals — to give you personalised college recommendations in seconds.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link href="/one-application" className="bg-white text-primary font-bold px-5 py-2.5 rounded-xl text-sm hover:bg-white/90 transition-colors flex items-center gap-2">
                    <Zap size={14} /> Try AI Matching
                  </Link>
                  <Link href="/college-predictor" className="bg-white/20 text-white font-bold px-5 py-2.5 rounded-xl text-sm hover:bg-white/30 transition-colors border border-white/30">
                    Predict Admission
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {aiFeatures.map((feat) => (
                  <div key={feat.title} className="bg-white/10 rounded-2xl p-4 border border-white/20">
                    <span className="text-2xl">{feat.icon}</span>
                    <p className="text-white font-bold text-sm mt-2 mb-1">{feat.title}</p>
                    <p className="text-white/70 text-xs leading-relaxed">{feat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── BROWSE BY STREAM ── */}
        <section className="py-10 px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Browse by Stream</h2>
              <p className="text-muted-foreground text-sm mt-1">Find colleges matching your academic interest</p>
            </div>
            <Link href="/" className="flex items-center gap-1 text-sm text-primary font-semibold hover:underline">
              View All <ChevronRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {topStreams.map((stream) => (
              <Link
                key={stream.name}
                href={`/?stream=${stream.name}`}
                className={`flex flex-col items-center gap-3 p-5 rounded-2xl border-2 ${stream.color} hover:shadow-md transition-all duration-200 hover:-translate-y-1 text-center`}
              >
                <div className="text-2xl">{streamIcons[stream.name]}</div>
                <div>
                  <p className="font-bold text-sm">{stream.name}</p>
                  <p className="text-xs opacity-70 mt-0.5">{stream.colleges.toLocaleString()} colleges</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ── TOP 50 COLLEGES ── */}
        <section className="py-14 bg-muted/50">
          <div className="px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Top 50 Colleges 2026</h2>
                <p className="text-muted-foreground text-sm mt-1">India's best colleges ranked by NIRF, placements & student reviews</p>
              </div>
              <Link href="/" className="flex items-center gap-1 text-sm text-primary font-semibold hover:underline">
                View All 50 <ChevronRight size={16} />
              </Link>
            </div>

            {compareList.length > 0 && (
              <div className="mb-4 flex items-center gap-3 p-3 bg-primary/10 rounded-xl border border-primary/20">
                <span className="text-sm font-semibold text-primary">{compareList.length}/3 colleges selected for comparison</span>
                {compareList.length >= 2 && (
                  <button onClick={handleCompareNow} className="btn-primary text-xs py-1.5 px-4 ml-auto">
                    Compare Now →
                  </button>
                )}
              </div>
            )}

            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-secondary text-white">
                      <th className="text-left px-4 py-3 font-bold text-xs w-12">Rank</th>
                      <th className="text-left px-4 py-3 font-bold text-xs">College</th>
                      <th className="text-left px-4 py-3 font-bold text-xs hidden sm:table-cell">Stream</th>
                      <th className="text-left px-4 py-3 font-bold text-xs hidden md:table-cell">NIRF</th>
                      <th className="text-left px-4 py-3 font-bold text-xs hidden lg:table-cell">Fees/yr</th>
                      <th className="text-left px-4 py-3 font-bold text-xs hidden lg:table-cell">Avg Pkg</th>
                      <th className="text-left px-4 py-3 font-bold text-xs hidden sm:table-cell">Rating</th>
                      <th className="text-center px-4 py-3 font-bold text-xs">Compare</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {top50Colleges.map((college) => (
                      <tr key={college.rank} className={`hover:bg-muted/50 transition-colors ${compareList.includes(college.rank) ? 'bg-accent' : ''}`}>
                        <td className="px-4 py-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-extrabold text-xs ${college.rank <= 3 ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>
                            #{college.rank}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-bold text-foreground">{college.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <MapPin size={10} /> {college.location}
                          </p>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-muted text-muted-foreground">{college.stream}</span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs font-bold text-primary">#{college.nirf}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs font-semibold text-foreground">{college.fees}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs font-bold text-green-600">{college.placement}</td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <div className="flex items-center gap-1">
                            <Star size={11} className="text-amber-400 fill-amber-400" />
                            <span className="text-xs font-bold">{college.rating}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => toggleCompare(college.rank)}
                            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center mx-auto transition-colors ${compareList.includes(college.rank) ? 'bg-primary border-primary text-white' : 'border-border hover:border-primary'}`}
                          >
                            {compareList.includes(college.rank) && <CheckCircle size={12} />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* ── CITY WISE SEARCH ── */}
        <section className="py-14">
          <div className="px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Colleges by City</h2>
                <p className="text-muted-foreground text-sm mt-1">Explore top institutions in your preferred city</p>
              </div>
              <Link href="/city-search" className="flex items-center gap-1 text-sm text-primary font-semibold hover:underline">
                All Cities <ChevronRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {topCities.map((city) => (
                <Link
                  key={city.name}
                  href={`/city-search?city=${city.name}`}
                  className="group flex flex-col items-center gap-2 p-4 bg-card rounded-2xl border border-border hover:border-primary hover:shadow-md transition-all duration-200 text-center"
                >
                  <span className="text-3xl">{city.icon}</span>
                  <p className="font-bold text-sm text-foreground group-hover:text-primary transition-colors">{city.name}</p>
                  <p className="text-xs text-muted-foreground">{city.count.toLocaleString()} colleges</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── TE RANKINGS ── */}
        <section className="py-14 bg-muted/50">
          <div className="px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-foreground">TE Rankings 2026</h2>
                <p className="text-muted-foreground text-sm mt-1">Times Education annual college ranking index</p>
              </div>
              <Link href="/" className="flex items-center gap-1 text-sm text-primary font-semibold hover:underline">
                Full Rankings <ChevronRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-2xl border border-border overflow-hidden">
                <div className="bg-secondary px-5 py-3 flex items-center gap-2">
                  <Award size={16} className="text-primary" />
                  <span className="text-white font-bold text-sm">Top Ranked Colleges — TE 2026</span>
                </div>
                <div className="divide-y divide-border">
                  {teRankings.map((item) => (
                    <div key={item.rank} className="flex items-center gap-4 px-5 py-4 hover:bg-muted/50 transition-colors">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-extrabold text-sm shrink-0 ${item.rank <= 3 ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>
                        #{item.rank}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-foreground truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground">{item.stream}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-sm text-foreground">{item.score}</p>
                        <p className="text-xs text-muted-foreground">TE Score</p>
                      </div>
                      <div className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${item.change === 'up' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {item.change === 'up' ? '↑' : '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick actions */}
              <div className="grid grid-cols-1 gap-4">
                {[
                  { href: '/compare-colleges', icon: <GraduationCap size={22} />, color: 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white', title: 'Compare Colleges', desc: 'Side-by-side comparison of fees, rankings & placements' },
                  { href: '/college-predictor', icon: <TrendingUp size={22} />, color: 'bg-violet-100 text-violet-600 group-hover:bg-primary group-hover:text-white', title: 'College Predictor', desc: 'Know your admission chances based on your score' },
                  { href: '/study-abroad', icon: <Globe size={22} />, color: 'bg-blue-100 text-blue-600 group-hover:bg-primary group-hover:text-white', title: 'Study Abroad', desc: 'Explore top universities in USA, UK, Canada & more' },
                  { href: '/college-to-career', icon: <Briefcase size={22} />, color: 'bg-green-100 text-green-600 group-hover:bg-primary group-hover:text-white', title: 'College to Career', desc: 'Map your career path from college to dream job' },
                ].map((item) => (
                  <Link key={item.href} href={item.href} className="group flex items-center gap-4 p-5 bg-card rounded-2xl border border-border hover:border-primary hover:shadow-md transition-all duration-200">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${item.color}`}>
                      {item.icon}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-foreground">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                    <ArrowRight size={18} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── TOP COLLEGES CARDS ── */}
        <section className="py-14">
          <div className="px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Featured Colleges 2026</h2>
                <p className="text-muted-foreground text-sm mt-1">Highest rated colleges based on NIRF & student reviews</p>
              </div>
              <Link href="/" className="flex items-center gap-1 text-sm text-primary font-semibold hover:underline">
                View All Colleges <ChevronRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {topColleges.map((college) => (
                <Link
                  key={college.id}
                  href="/"
                  className="group bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                >
                  <div className="relative h-36 overflow-hidden">
                    <img
                      src={college.image}
                      alt={`${college.name} campus building`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute top-3 left-3 flex gap-1.5">
                      {college.nirfRank && (
                        <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full">NIRF #{college.nirfRank}</span>
                      )}
                    </div>
                    <div className="absolute bottom-3 right-3 bg-white/95 rounded-lg px-2 py-1 flex items-center gap-1">
                      <Star size={11} className="text-amber-400 fill-amber-400" />
                      <span className="text-xs font-bold text-foreground">{college.rating}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-sm text-foreground line-clamp-1 group-hover:text-primary transition-colors">{college.name}</h3>
                    <div className="flex items-center gap-1 text-muted-foreground mt-1 mb-3">
                      <MapPin size={11} />
                      <span className="text-xs">{college.location}, {college.state}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-muted rounded-lg py-1.5">
                        <p className="font-bold text-foreground">
                          {college.totalFees >= 100000 ? `₹${(college.totalFees / 100000).toFixed(1)}L` : `₹${(college.totalFees / 1000).toFixed(0)}K`}
                        </p>
                        <p className="text-muted-foreground">Fees</p>
                      </div>
                      <div className="bg-muted rounded-lg py-1.5">
                        <p className="font-bold text-primary">{college.cutoffScore}</p>
                        <p className="text-muted-foreground">Cutoff</p>
                      </div>
                      <div className="bg-muted rounded-lg py-1.5">
                        <p className="font-bold text-foreground">
                          {college.placementAvgPackage >= 10000000 ? `₹${(college.placementAvgPackage / 10000000).toFixed(1)}Cr` : `₹${(college.placementAvgPackage / 100000).toFixed(1)}L`}
                        </p>
                        <p className="text-muted-foreground">Avg Pkg</p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── STUDENT COUNSELLING CTA ── */}
        <section className="py-14 bg-muted/50">
          <div className="px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
            <div className="bg-secondary rounded-3xl overflow-hidden relative">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-primary blur-3xl" />
              </div>
              <div className="relative px-8 py-12 lg:px-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-primary/20 rounded-full px-4 py-1.5 text-sm text-primary mb-5">
                      <Users size={14} />
                      <span>Free Student Counselling</span>
                    </div>
                    <h2 className="text-3xl font-extrabold text-white mb-4">
                      Not Sure Which College to Choose?
                    </h2>
                    <p className="text-slate-300 mb-6 leading-relaxed">
                      Our certified counsellors have helped 5 lakh+ students find the right college. Get personalised guidance — completely free.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Link href="/counselling" className="btn-primary">Book Free Session</Link>
                      <Link href="/counselling" className="btn-secondary border-white/30 text-white hover:bg-white/10">Learn More</Link>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {counsellingSteps.map((step) => (
                      <div key={step.step} className="bg-white/10 rounded-2xl p-4 border border-white/10">
                        <span className="text-primary font-extrabold text-2xl">{step.step}</span>
                        <p className="text-white font-bold text-sm mt-2 mb-1">{step.title}</p>
                        <p className="text-slate-300 text-xs leading-relaxed">{step.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── NEWSLETTER ── */}
        <section className="py-14 px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
          <div className="bg-gradient-to-br from-accent via-background to-accent rounded-3xl border border-primary/20 p-8 lg:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 left-1/4 w-48 h-48 rounded-full bg-primary blur-3xl -translate-y-1/2" />
              <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full bg-violet-400 blur-3xl translate-y-1/2" />
            </div>
            <div className="relative max-w-xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-1.5 text-sm text-primary mb-5">
                <Send size={14} />
                <span>Weekly College Updates</span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-extrabold text-foreground mb-3">
                Stay Ahead with All About Campus Newsletter
              </h2>
              <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                Get weekly updates on college rankings, exam dates, scholarship alerts, and career insights — delivered to your inbox.
              </p>
              {newsletterSubmitted ? (
                <div className="flex items-center justify-center gap-3 p-4 bg-green-50 rounded-2xl border border-green-200">
                  <CheckCircle size={20} className="text-green-600" />
                  <p className="font-semibold text-green-700">You&apos;re subscribed! Check your inbox for a welcome email.</p>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleNewsletter()}
                    className="input-base flex-1"
                  />
                  <button onClick={handleNewsletter} className="btn-primary px-6 py-2.5 whitespace-nowrap">
                    Subscribe Free
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-3">No spam. Unsubscribe anytime. 1.2L+ subscribers.</p>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
